import {
  ArrowDownOnSquareIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  BuildingLibraryIcon,
  CurrencyDollarIcon,
  TruckIcon,
} from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Suspense } from "react";
import { useSelector } from "react-redux";

import { selectLockedOut, selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import { selectListOfMenuResourcesWithBalanceRestriction } from "@/redux/positions/positionsSelectors";
import { useGetClientRequestSummaryQuery, useGetTasksQuery } from "@/routes/_authenticated/trade/-api/trade";
import LockoutAlert from "@/routes/_authenticated/trade/-components/LockoutAlert";
import { useGetAccountPositionQuery } from "@/shared/api/account";
import MenuCard from "@/shared/components/MenuCard";
import ApiErrorBoundary from "@/shared/components/route-state/ApiErrorBoundary";
import { CircularProgress } from "@/shared/components/ui/circular-progress";
import { Skeleton } from "@/shared/components/ui/skeleton";

const TRADE_PAGES = [
  {
    titleKey: t`Purchase`,
    path: "/trade/purchase",
    menuAcl: "trade.purchase",
    icon: <CurrencyDollarIcon className="h-5 w-auto" />,
  },
  {
    titleKey: t`Ship To Vault`,
    path: "/trade/ship-metal",
    menuAcl: "trade.ship",
    icon: <BuildingLibraryIcon className="h-5 w-auto" />,
  },
  {
    titleKey: t`Vault Transfer`,
    path: "/trade/send/vault-transfer",
    menuAcl: "trade.send",
    icon: <TruckIcon className="h-5 w-auto" />,
  },
  {
    titleKey: t`Metal Transfer`,
    path: "/trade/metal-transfer",
    menuAcl: "trade.metal-transfer",
    icon: <ArrowsRightLeftIcon className="h-5 w-auto" />,
  },
  {
    titleKey: t`Withdraw`,
    path: "/trade/withdraw",
    menuAcl: "trade.withdraw",
    icon: <ArrowDownOnSquareIcon className="h-5 w-auto" />,
  },
  {
    titleKey: t`Recent Transactions`,
    path: "/trade/pending",
    menuAcl: "trade.pending",
    icon: <ArrowPathIcon className="h-5 w-auto" />,
  },
];

const getTradePages = (aclMenu: string[]) => TRADE_PAGES.filter((page) => aclMenu.includes(page.menuAcl));

const TradeSkeleton = () => <Skeleton className="h-64 rounded-2xl" />;

function TradeView() {
  const eid = useSelector(selectMaybeActiveAccountEid);
  const aclMenu = useSelector(selectListOfMenuResourcesWithBalanceRestriction);
  const isLockedOut = useSelector(selectLockedOut);
  const tradePages = getTradePages(aclMenu);
  const positionQuery = useGetAccountPositionQuery(eid || skipToken);

  // prefetch tasks and client request summary
  useGetTasksQuery(eid || skipToken);
  useGetClientRequestSummaryQuery(eid || skipToken);

  return (
    <ApiErrorBoundary
      LoadingSkeleton={<TradeSkeleton />}
      shouldRetry
      queries={[positionQuery]}
      translatedErrorMessage="Error loading balances"
      loadIndicator="isLoading"
    >
      {isLockedOut && <LockoutAlert />}
      <MenuCard menuItems={tradePages} menuLabel={t`Select a Transaction Type`}>
        <div className="relative min-h-[200px] w-full p-6">
          <Suspense fallback={<CircularProgress className="absolute inset-0 m-auto" />}>
            <Outlet />
          </Suspense>
        </div>
      </MenuCard>
    </ApiErrorBoundary>
  );
}

export const Route = createFileRoute("/_authenticated/trade")({
  component: TradeView,
  context: () => ({ permissions: "trade" }),
});
